/**
 * Memoized selectors for reading streaming state from Redux.
 *
 * Selectors provide a clean interface for components to read streaming data
 * without directly accessing the Redux state structure.
 *
 * All selectors are memoized using createSelector for optimal performance.
 */

import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../types/redux';
import type { StreamingMessage, ParserState, ChunkMetadata } from './types';

/**
 * Base selector to get the streaming state slice.
 * This is the root selector that all other selectors build upon.
 *
 * Note: This will return undefined until Phase 2 when we add the streaming
 * slice to the Redux store. This is intentional - Phase 1 is just structure.
 */
const selectStreamingState = (state: RootState) => {
  return (state as any).streaming;
};

/**
 * Select all streaming messages.
 *
 * @param state - Redux root state
 * @returns Object containing all streaming messages keyed by messageId
 */
export const selectAllStreamingMessages = createSelector(
  [selectStreamingState],
  (streaming) => streaming?.messages || {}
);

/**
 * Select a specific streaming message by ID.
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns The streaming message, or undefined if not found
 */
export const selectStreamingMessage = createSelector(
  [
    selectAllStreamingMessages,
    (_state: RootState, messageId: string) => messageId,
  ],
  (messages, messageId): StreamingMessage | undefined => messages[messageId]
);

/**
 * Select the displayed text for a message.
 *
 * This is the text that should be shown to the user (animated).
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns The displayed text, or empty string if not found
 */
export const selectDisplayedText = createSelector(
  [selectStreamingMessage],
  (message): string => message?.displayedText || ''
);

/**
 * Select whether a message is currently animating.
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns True if the message is animating, false otherwise
 */
export const selectIsAnimating = createSelector(
  [selectStreamingMessage],
  (message): boolean => message?.isAnimating || false
);

/**
 * Select whether a message stream is complete.
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns True if no more chunks will arrive, false otherwise
 */
export const selectIsComplete = createSelector(
  [selectStreamingMessage],
  (message): boolean => message?.isComplete || false
);

/**
 * Select the parser state for a message.
 *
 * This includes the parser's buffer, pending fact status, and rendered output.
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns The parser state, or undefined if not found
 */
export const selectParserState = createSelector(
  [selectStreamingMessage],
  (message): ParserState | undefined => message?.parserState
);

/**
 * Select the pending chunks for a message.
 *
 * These are chunks waiting to be animated.
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns Array of pending chunks, or empty array if none
 */
export const selectPendingChunks = createSelector(
  [selectStreamingMessage],
  (message): ChunkMetadata[] => message?.pendingChunks || []
);

/**
 * Select the full text for a message.
 *
 * This is all the text received from the backend (not yet animated).
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns The full text, or empty string if not found
 */
export const selectFullText = createSelector(
  [selectStreamingMessage],
  (message): string => message?.fullText || ''
);

/**
 * Select the version number for a message.
 *
 * The version is incremented each time a chunk arrives,
 * allowing the animation system to detect stale work.
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns The version number, or 0 if not found
 */
export const selectVersion = createSelector(
  [selectStreamingMessage],
  (message): number => message?.version || 0
);

/**
 * Select whether a message has pending chunks to animate.
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns True if there are pending chunks, false otherwise
 */
export const selectHasPendingChunks = createSelector(
  [selectPendingChunks],
  (chunks): boolean => chunks.length > 0
);

/**
 * Select the number of pending chunks for a message.
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns The number of pending chunks
 */
export const selectPendingChunkCount = createSelector(
  [selectPendingChunks],
  (chunks): number => chunks.length
);

/**
 * Select whether a message needs animation.
 *
 * A message needs animation if:
 * - It has pending chunks, OR
 * - The displayed text is behind the full text
 *
 * @param state - Redux root state
 * @param messageId - The unique message identifier
 * @returns True if animation is needed, false otherwise
 */
export const selectNeedsAnimation = createSelector(
  [selectStreamingMessage],
  (message): boolean => {
    if (!message) return false;
    return (
      message.pendingChunks.length > 0 ||
      message.displayedText.length < message.fullText.length
    );
  }
);

/**
 * Select all message IDs that are currently streaming.
 *
 * @param state - Redux root state
 * @returns Array of message IDs that are streaming
 */
export const selectStreamingMessageIds = createSelector(
  [selectAllStreamingMessages],
  (messages): string[] => {
    return Object.keys(messages).filter(
      (id) => messages[id].isAnimating || !messages[id].isComplete
    );
  }
);
